import React from 'react';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import getMoveToFolderTooltip from './get-move-to-folder-tooltip';
import { ROOT_ID } from '../constants/index.js';

const { Paragraph } = designSystem;

// eslint-disable-next-line yola/react/prop-types
const DeleteTitleComponent = ({ children }) => (
  <Paragraph appearance="danger">{children}</Paragraph>
);

const buildMoveToFolderAction = ({ disabled, tooltip, onClick }) => ({
  id: 'moveToFolder',
  iconGlyph: 'folder-move',
  title: `${i18next.t('Move to')}...`,
  disabled,
  ...(disabled && { tooltip }),
  onClick,
});

const buildPageActions = (item, context) => {
  const {
    isHome,
    isActive,
    isDeveloperConsoleEnabled,
    aiGeneratePageCopyEnabled,
    isPageFoldersEnabled,
    moveToFolderTooltip,
    onSetHomePageClick,
    onCopyPageClick,
    onPageSettingsClick,
    onTranslatePageClick,
    onGeneratePageCopyClick,
    onDeveloperConsoleClick,
    onDeletePageClick,
    onMoveToFolderClick,
  } = context;

  const isDeveloperConsoleAvailable = isDeveloperConsoleEnabled && isActive;

  const actions = [];

  if (!isHome) {
    actions.push({
      id: 'setHomePage',
      iconGlyph: 'home',
      divider: 'after',
      title: i18next.t('Set Home Page'),
      onClick: (e) => onSetHomePageClick(e, item),
    });
  }

  actions.push({
    id: 'copyPage',
    iconGlyph: 'copy',
    title: `${i18next.t('Duplicate')}...`,
    onClick: (e) => onCopyPageClick(e, item.id),
  });

  actions.push({
    id: 'pageSettings',
    iconGlyph: 'settings',
    title: `${i18next.t('Settings')}...`,
    onClick: (e) => onPageSettingsClick(e, item),
  });

  actions.push({
    id: 'translatePage',
    iconGlyph: 'translate',
    title: `${i18next.t('Translate to')}...`,
    onClick: (e) => onTranslatePageClick(e, item.id),
  });

  if (isPageFoldersEnabled) {
    actions.push(
      buildMoveToFolderAction({
        disabled: Boolean(moveToFolderTooltip),
        tooltip: moveToFolderTooltip,
        onClick: (e) => onMoveToFolderClick(e, { pageIds: [item.id], folderIds: [] }),
      })
    );
  }

  if (aiGeneratePageCopyEnabled) {
    actions.push({
      id: 'generatePageCopy',
      iconGlyph: 'ai',
      title: `${i18next.t('Generate page copy')}...`,
      onClick: () => onGeneratePageCopyClick(item.id),
    });
  }

  if (isDeveloperConsoleAvailable) {
    actions.push({
      id: 'developerConsole',
      iconGlyph: 'html',
      divider: 'before',
      title: `${i18next.t('Developer console')}...`,
      onClick: (e) => onDeveloperConsoleClick(e, item),
    });
  }

  if (!isHome) {
    actions.push({
      id: 'deletePage',
      iconGlyph: 'trash',
      iconType: 'error',
      divider: 'before',
      component: DeleteTitleComponent,
      title: i18next.t('Delete'),
      onClick: (e) => onDeletePageClick(e, item),
    });
  }

  return actions;
};

const buildFolderActions = (
  folder,
  context,
  { containsHomePage = false, moveToFolderTooltip = null } = {}
) => {
  const { onFolderSettingsClick, onDeleteFolderClick, onMoveToFolderClick } = context;

  return [
    {
      id: 'folderSettings',
      iconGlyph: 'settings',
      title: `${i18next.t('Settings')}...`,
      onClick: (e) => onFolderSettingsClick(e, folder),
    },
    buildMoveToFolderAction({
      disabled: Boolean(moveToFolderTooltip),
      tooltip: moveToFolderTooltip,
      onClick: (e) => onMoveToFolderClick(e, { pageIds: [], folderIds: [folder.id] }),
    }),
    {
      id: 'deleteFolder',
      iconGlyph: 'trash',
      iconType: 'error',
      divider: 'before',
      title: i18next.t('Delete'),
      // A folder can't be deleted while it holds the home page (directly or in a
      // nested subfolder), since deleting it would delete the home page. When it
      // can be deleted the title is red; when disabled it falls back to the
      // default grey title (and grey icon) of the disabled state.
      disabled: containsHomePage,
      ...(containsHomePage
        ? {
            tooltip: (
              <>
                {i18next.t("Home page can't be deleted")}
                <br />
                {i18next.t('Deselect it to delete the rest')}
              </>
            ),
          }
        : { component: DeleteTitleComponent }),
      onClick: (e) => onDeleteFolderClick(e, folder),
    },
  ];
};

const buildPageTreeItems = (items, context) => {
  const { folders = [], onAddPageToFolder, allPages, allFolders } = context;
  const folderIds = new Set(folders.map((folder) => folder.id));
  const pagesForEmptyFolders = allPages ?? items;
  // Move targets are counted over every folder of the locale, not only the ones
  // left in the tree by the search filter.
  const foldersForMoveTargets = allFolders ?? folders;
  const getItemMoveTooltip = (ids) =>
    getMoveToFolderTooltip({
      ...ids,
      folders: foldersForMoveTargets,
      pages: pagesForEmptyFolders,
    });

  // Folder ids that contain the home page — the chain of folders from the home
  // page up to the root — so their delete action can be disabled.
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
  const homePage = pagesForEmptyFolders.find((page) => context.isHomePage(page));
  const homePageFolderIds = new Set();
  let homeFolderId = homePage?.parentFolderId;
  while (homeFolderId) {
    homePageFolderIds.add(homeFolderId);
    homeFolderId = foldersById.get(homeFolderId)?.parentFolderId;
  }

  const emptyFolderIds = new Set(
    folders
      .filter(
        (folder) =>
          pagesForEmptyFolders.every((page) => page.parentFolderId !== folder.id) &&
          folders.every((other) => other.parentFolderId !== folder.id)
      )
      .map((folder) => folder.id)
  );

  const sortedFolders = [...folders].sort((a, b) => {
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();
    return aName.localeCompare(bName);
  });

  const folderNodes = sortedFolders.map((folder) => {
    const isFolderEmpty = emptyFolderIds.has(folder.id);
    const folderData = { ...folder, isFolderEmpty };
    return {
      id: folder.id,
      parent: folder.parentFolderId || ROOT_ID,
      text: folder.name,
      droppable: true,
      draggable: true,
      collapsed: true,
      data: {
        ...folderData,
        type: 'folder',
        iconGlyph: 'folder',
        isFolder: true,
        actions: buildFolderActions(folderData, context, {
          containsHomePage: homePageFolderIds.has(folder.id),
          moveToFolderTooltip: getItemMoveTooltip({ folderIds: [folder.id] }),
        }),
        onAddPageToFolder: (e) => onAddPageToFolder(e, folder.id),
      },
    };
  });

  const itemsWithMeta = items.map((item) => ({
    item,
    isHome: context.isHomePage(item),
    isActive: item.id === context.activePageId,
  }));

  const sortedItems = [...itemsWithMeta].sort((a, b) => {
    if (a.isHome) return -1;
    if (b.isHome) return 1;
    const aName = (a.item.name || '').toLowerCase();
    const bName = (b.item.name || '').toLowerCase();
    return aName.localeCompare(bName);
  });

  const pageNodes = sortedItems.map(({ item, isHome, isActive }) => ({
    id: item.id,
    parent:
      item.parentFolderId && folderIds.has(item.parentFolderId) ? item.parentFolderId : ROOT_ID,
    text: item.title || item.name,
    droppable: false,
    draggable: true,
    data: {
      ...item,
      isActive,
      iconGlyph: isHome ? 'home' : 'page',
      actions: buildPageActions(item, {
        ...context,
        isHome,
        isActive,
        moveToFolderTooltip: getItemMoveTooltip({ pageIds: [item.id] }),
      }),
    },
  }));

  return [...pageNodes, ...folderNodes];
};

export default buildPageTreeItems;
